import { AlertCircle, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ErrorRendererProps {
  data: unknown
}

interface ParsedError {
  message: string
  code?: string
  details?: string[]
  hint?: string
}

function parseError(data: unknown): ParsedError {
  // Handle string errors
  if (typeof data === 'string') {
    return { message: data }
  }

  // Handle array of validation errors (FastAPI style)
  if (Array.isArray(data)) {
    const messages = data.map((err) => {
      if (typeof err === 'string') return err
      if (err.msg) return `${err.loc?.join('.') || 'Field'}: ${err.msg}`
      return JSON.stringify(err)
    })
    return {
      message: 'Validation Error',
      details: messages,
    }
  }

  // Handle object errors
  if (typeof data === 'object' && data !== null) {
    const obj = data as Record<string, unknown>

    // Handle nested error object pattern: {error: {error_code, message, ...}, status_code}
    if (typeof obj.error === 'object' && obj.error !== null) {
      const innerError = obj.error as Record<string, unknown>
      const message =
        typeof innerError.message === 'string'
          ? innerError.message
          : typeof innerError.msg === 'string'
            ? innerError.msg
            : 'An error occurred'
      const code =
        typeof innerError.error_code === 'string'
          ? innerError.error_code
          : typeof obj.status_code === 'number'
            ? String(obj.status_code)
            : undefined
      return { message, code }
    }

    // Check for common error fields
    const rawMessage = obj.message ?? obj.error ?? obj.detail ?? obj.msg ?? 'An error occurred'

    const message = typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage)

    const rawCode = obj.code ?? obj.error_code ?? obj.status_code
    const code =
      typeof rawCode === 'string' ? rawCode : rawCode != null ? String(rawCode) : undefined

    const hint =
      typeof obj._hint === 'string'
        ? obj._hint
        : typeof obj.hint === 'string'
          ? obj.hint
          : undefined

    // Handle nested detail array
    let details: string[] | undefined
    if (Array.isArray(obj.detail)) {
      details = obj.detail.map((d: unknown) => {
        if (typeof d === 'string') return d
        if (typeof d === 'object' && d !== null) {
          const detail = d as Record<string, unknown>
          return `${(detail.loc as string[])?.join('.') || 'Field'}: ${detail.msg}`
        }
        return String(d)
      })
    }

    return { message, code, details, hint }
  }

  return { message: 'Unknown error' }
}

export function ErrorRenderer({ data }: ErrorRendererProps) {
  const error = parseError(data)
  const isValidationError = error.details && error.details.length > 0

  return (
    <div
      className={cn('rounded-lg border p-3 space-y-2', 'bg-destructive/5 border-destructive/30')}
    >
      {/* Header */}
      <div className="flex items-start gap-2">
        {isValidationError ? (
          <AlertTriangle className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />
        ) : (
          <AlertCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm text-destructive">{error.message}</span>
            {error.code && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-destructive/10 text-destructive font-mono">
                {error.code}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Validation details */}
      {error.details && error.details.length > 0 && (
        <ul className="text-xs space-y-1 ml-6 text-muted-foreground">
          {error.details.map((detail, i) => (
            <li key={i} className="list-disc list-inside">
              {detail}
            </li>
          ))}
        </ul>
      )}

      {/* Hint */}
      {error.hint && (
        <p className="text-xs text-muted-foreground ml-6 italic">Hint: {error.hint}</p>
      )}
    </div>
  )
}
